import React, { useState, useEffect } from 'react';
import { Modal, Button } from 'react-bootstrap';
import axios from "axios";
import useHandleErrors from '../../utils/user.utils';

const AddPartnerModal = ({ show, onHide, onSubmit }) => {
    const [formData, setFormData] = useState({
        partnerName: '',
        registrationNumber: '',
        partnerType: '',
        vatNumber: '',
        contactNumber: '',
        email: '',
        partnerManager: '',
        address: '',
        bankName: '',
        accountNumber: '',
        sortCode: '',
        iban: '',
        swift: '',
        remarks: '',
        UserType: 'Partner',
        logo: null
    });

    const [errors, setErrors] = useState({});
    const [loading, setLoading] = useState(false);
    const [logoPreview, setLogoPreview] = useState(null);
    const [organisationPartnerDetails, setOrganisationPartnerDetails] = useState(null);
    const [partnerTypes, setPartnerTypes] = useState([]);
    const token = localStorage.getItem("token");
    const [managerEmails, setManagerEmails] = useState([]);
    const handleErrors = useHandleErrors();

    // Fetch organisation/vendor details and partner types for Partner Type dropdown only when modal is shown
    useEffect(() => {
        if (!show || !token) return;
        const fetchOrganisationVendorDetails = async () => {
            try {
                const url = `${process.env.REACT_APP_BE_URL}/getConfigurationCenter`;
                const response = await axios.get(url, {
                    headers: { Authorization: `Bearer ${token}` },
                });
                if (response.status === 200 && response.data) {
                    if (response.data.partnerDetails) {
                        setOrganisationPartnerDetails(response.data.partnerDetails);
                        console.log("partnerDetails:", response.data.partnerDetails);
                    } else {
                        setOrganisationPartnerDetails(null);
                    }
                    // Set partner types from API response
                    if (response.data.partnerManagement && Array.isArray(response.data.partnerManagement.partnerTypes)) {
                        setPartnerTypes(response.data.partnerManagement.partnerTypes);
                    } else {
                        setPartnerTypes([]);
                    }
                } else {
                    setOrganisationPartnerDetails(null);
                    setPartnerTypes([]);
                }
            } catch (error) {
                console.error('Failed to fetch organisation/vendor details:', error);
                setOrganisationPartnerDetails(null);
                setPartnerTypes([]);
            }
        };
        fetchOrganisationVendorDetails();
    }, [show, token]);



    const handleChange = (e) => {
        const { name, value, type, files } = e.target;
        if (type === 'file') {
            const file = files[0];
            setFormData(prev => ({
                ...prev,
                [name]: file
            }));
            // Show preview
            if (file) {
                const reader = new FileReader();
                reader.onloadend = () => {
                    setLogoPreview(reader.result);
                };
                reader.readAsDataURL(file);
            } else {
                setLogoPreview(null);
            }
        } else {
            setFormData(prev => ({
                ...prev,
                [name]: value
            }));
        }

        // Clear error when user starts typing
        if (errors[name]) {
            setErrors(prev => ({
                ...prev,
                [name]: ''
            }));
        }
    };

    const validateForm = () => {
        const newErrors = {};

        if (!formData.partnerName.trim()) {
            newErrors.partnerName = 'Partner name is required';
        }

        if (!formData.registrationNumber.trim()) {
            newErrors.registrationNumber = 'Registration number is required';
        }

        if (!formData.partnerType) {
            newErrors.partnerType = 'Partner type is required';
        }

        if (!formData.contactNumber.trim()) {
            newErrors.contactNumber = 'Contact number is required';
        }

        if (!formData.email.trim()) {
            newErrors.email = 'Email is required';
        } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
            newErrors.email = 'Email is invalid';
        }

        if (!formData.partnerManager.trim()) {
            newErrors.partnerManager = 'Partner manager is required';
        }

        if (!formData.address.trim()) {
            newErrors.address = 'Address is required';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validateForm()) {
            return;
        }

        setLoading(true);

        try {
            const submitData = new FormData();

            // Map form fields to API payload format
            const fieldMapping = {
                partnerName: 'PartnerName',
                registrationNumber: 'RegistrationNumber',
                partnerType: 'PartnerType',
                vatNumber: 'VatNumber',
                contactNumber: 'ContactNumber',
                email: 'Email',
                partnerManager: 'PartnerManager',
                address: 'Address',
                bankName: 'BankName',
                accountNumber: 'AccountNumber',
                sortCode: 'SortCode',
                iban: 'IBAN',
                swift: 'SWIFT',
                remarks: 'RemarksNote',
                logo: 'logoUpload',
                UserType: 'UserType'
            };

            Object.keys(formData).forEach(key => {
                if (formData[key] !== null && formData[key] !== '') {
                    const apiKey = fieldMapping[key] || key;
                    submitData.append(apiKey, formData[key]);
                }
            });

            await onSubmit(submitData);
            handleReset();
        } catch (error) {
            console.error('Error submitting form:', error);
        } finally {
            setLoading(false);
        }
    };

    // Fetch employees and get unique emails
    useEffect(() => {
        if (show) fetchEmployees();
        // eslint-disable-next-line
    }, [show]); // Only fetch when modal is opened

    async function fetchEmployees() {
        setLoading(true);
        try {
            const URL = process.env.REACT_APP_BE_URL + "/getEmployeeMasterReport";
            const response = await axios.get(URL, {
                headers: { Authorization: `Bearer ${token}` }
            });
            if (response.status === 200 && response.data.onboardEmployeeDetails) {
                const activeEmployees = response.data.onboardEmployeeDetails.filter(emp => emp.Status === "Active");
                setManagerEmails(
                    Array.from(new Set(
                        activeEmployees.map(emp => emp.OfficialEmail).filter(Boolean)
                    ))
                );
            } else {
                handleErrors(response);
            }
        } catch (err) {
            if (err.response) handleErrors(err.response);
        } finally { setLoading(false); }
    }

    const handleReset = () => {
        setFormData({
            partnerName: '',
            registrationNumber: '',
            partnerType: '',
            vatNumber: '',
            contactNumber: '',
            email: '',
            partnerManager: '',
            address: '',
            bankName: '',
            accountNumber: '',
            sortCode: '',
            iban: '',
            swift: '',
            remarks: '',
            logo: null,
            UserType: 'Partner'
        });
        setErrors({});
        setLogoPreview(null);
    };

    const handleClose = () => {
        handleReset();
        onHide();
    };

    return (
        <Modal show={show} onHide={handleClose} size="lg" backdrop="static">
            <Modal.Header closeButton>
                <Modal.Title>Add New Partner</Modal.Title>
            </Modal.Header>
            <form onSubmit={handleSubmit}>
                <Modal.Body style={{ maxHeight: '70vh', overflowY: 'auto' }}>
                    <div className="row">
                        {/* Basic Information */}
                        <div className="col-12">
                            <h6 className="mb-3 text-primary">Basic Information</h6>
                        </div>

                        <div className="col-md-6 mb-3">
                            <label className="form-label">Partner Name *</label>
                            <input
                                type="text"
                                className={`form-control ${errors.partnerName ? 'is-invalid' : ''}`}
                                name="partnerName"
                                value={formData.partnerName}
                                onChange={handleChange}
                                placeholder="Enter partner name"
                            />
                            {errors.partnerName && <div className="invalid-feedback">{errors.partnerName}</div>}
                        </div>

                        <div className="col-md-6 mb-3">
                            <label className="form-label">Registration Number *</label>
                            <input
                                type="text"
                                className={`form-control ${errors.registrationNumber ? 'is-invalid' : ''}`}
                                name="registrationNumber"
                                value={formData.registrationNumber}
                                onChange={handleChange}
                                placeholder="Enter registration number"
                            />
                            {errors.registrationNumber && <div className="invalid-feedback">{errors.registrationNumber}</div>}
                        </div>

                        <div className="col-md-6 mb-3">
                            <label className="form-label">Partner Type *</label>
                            <select
                                className={`form-control ${errors.partnerType ? 'is-invalid' : ''}`}
                                name="partnerType"
                                value={formData.partnerType}
                                onChange={handleChange}
                                placeholder="Select partner type"
                            >
                                <option value="">Select partner type</option>
                                {partnerTypes && partnerTypes.length > 0 ? (
                                    partnerTypes.map(type => (
                                        <option key={type} value={type}>{type}</option>
                                    ))
                                ) : (
                                    <option value="" disabled>No partner types available</option>
                                )}
                            </select>
                            {errors.partnerType && <div className="invalid-feedback">{errors.partnerType}</div>}
                        </div>

                        <div className="col-md-6 mb-3">
                            <label className="form-label">VAT Number</label>
                            <input
                                type="text"
                                className="form-control"
                                name="vatNumber"
                                value={formData.vatNumber}
                                onChange={handleChange}
                                placeholder="Enter VAT number"
                            />
                        </div>

                        {/* Contact Information */}
                        <div className="col-12 mt-3">
                            <h6 className="mb-3 text-primary">Contact Information</h6>
                        </div>

                        <div className="col-md-6 mb-3">
                            <label className="form-label">Contact Number *</label>
                            <input
                                type="number"
                                className={`form-control ${errors.contactNumber ? 'is-invalid' : ''}`}
                                name="contactNumber"
                                value={formData.contactNumber}
                                onChange={handleChange}
                                placeholder="Enter contact number"
                            />
                            {errors.contactNumber && <div className="invalid-feedback">{errors.contactNumber}</div>}
                        </div>

                        <div className="col-md-6 mb-3">
                            <label className="form-label">Email *</label>
                            <input
                                type="email"
                                className={`form-control ${errors.email ? 'is-invalid' : ''}`}
                                name="email"
                                value={formData.email}
                                onChange={handleChange}
                                placeholder="Enter email address"
                            />
                            {errors.email && <div className="invalid-feedback">{errors.email}</div>}
                        </div>

                        <div className="col-md-6 mb-3">
                            <label className="form-label"> In House Partner Manager *</label>
                            <select
                                className={`form-control ${errors.partnerManager ? 'is-invalid' : ''}`}
                                name="partnerManager"
                                value={formData.partnerManager}
                                onChange={handleChange}
                            >
                                <option value="">Select Email</option>
                                {managerEmails.map(email => (
                                    <option key={email} value={email}>{email}</option>
                                ))}
                            </select>
                            {errors.partnerManager && <div className="invalid-feedback">{errors.partnerManager}</div>}
                        </div>

                        <div className="col-12 mb-3">
                            <label className="form-label">Address *</label>
                            <textarea
                                className={`form-control ${errors.address ? 'is-invalid' : ''}`}
                                name="address"
                                value={formData.address}
                                onChange={handleChange}
                                rows="3"
                                placeholder="Enter complete address"
                            />
                            {errors.address && <div className="invalid-feedback">{errors.address}</div>}
                        </div>

                        {/* Banking Details */}
                        <div className="col-12 mt-3">
                            <h6 className="mb-3 text-primary">Banking Details</h6>
                        </div>

                        <div className="col-md-6 mb-3">
                            <label className="form-label">Bank Name</label>
                            <input
                                type="text"
                                className="form-control"
                                name="bankName"
                                value={formData.bankName}
                                onChange={handleChange}
                                placeholder="Enter bank name"
                            />
                        </div>

                        <div className="col-md-6 mb-3">
                            <label className="form-label">Account Number</label>
                            <input
                                type="text"
                                className="form-control"
                                name="accountNumber"
                                value={formData.accountNumber}
                                onChange={handleChange}
                                placeholder="Enter account number"
                            />
                        </div>

                        <div className="col-md-6 mb-3">
                            <label className="form-label">Sort Code*</label>
                            <input
                                type="text"
                                className="form-control"
                                name="sortCode"
                                value={formData.sortCode}
                                onChange={handleChange}
                                placeholder="Enter sort code"
                            />
                        </div>

                        <div className="col-md-6 mb-3">
                            <label className="form-label">IBAN</label>
                            <input
                                type="text"
                                className="form-control"
                                name="iban"
                                value={formData.iban}
                                onChange={handleChange}
                                placeholder="Enter IBAN"
                            />
                        </div>

                        <div className="col-md-6 mb-3">
                            <label className="form-label">SWIFT</label>
                            <input
                                type="text"
                                className="form-control"
                                name="swift"
                                value={formData.swift}
                                onChange={handleChange}
                                placeholder="Enter SWIFT code"
                            />
                        </div>

                        {/* Additional Information */}
                        <div className="col-12 mt-3">
                            <h6 className="mb-3 text-primary">Additional Information*</h6>
                        </div>

                        <div className="col-12 mb-3">
                            <label className="form-label">Remarks/Notes*</label>
                            <textarea
                                className="form-control"
                                name="remarks"
                                value={formData.remarks}
                                onChange={handleChange}
                                rows="3"
                                placeholder="Enter any remarks or notes"
                            />
                        </div>

                        <div className="col-md-6 mb-3">
                            <label className="form-label">Upload Logo</label>
                            <input
                                type="file"
                                className="form-control"
                                name="logo"
                                onChange={handleChange}
                                accept="image/*"
                            />
                            <small className="form-text text-muted">
                                Accepted formats: JPG, PNG, GIF
                            </small>
                            {logoPreview && (
                                <div className="mt-2">
                                    <img
                                        src={logoPreview}
                                        alt="Logo Preview"
                                        style={{ maxWidth: '120px', maxHeight: '120px', border: '1px solid #ddd', borderRadius: 6 }}
                                    />
                                </div>
                            )}
                        </div>
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={handleClose} disabled={loading}>
                        Cancel
                    </Button>
                    <Button variant="primary" type="submit" disabled={loading}>
                        {loading ? (
                            <>
                                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                Creating...
                            </>
                        ) : (
                            'Create Partner'
                        )}
                    </Button>
                </Modal.Footer>
            </form>
        </Modal>
    );
};

export default AddPartnerModal;
